import { db } from '../firebase';
import { 
  doc, 
  updateDoc, 
  increment, 
  collection, 
  addDoc, 
  getDoc, 
  setDoc, 
  Timestamp,
  runTransaction
} from 'firebase/firestore';

// Initialize metrics in shop document if they don't exist
const initializeShopMetrics = async (shopRef) => {
  const shopDoc = await getDoc(shopRef);
  if (!shopDoc.exists()) return false;

  const shopData = shopDoc.data();
  if (!shopData.metrics) {
    await updateDoc(shopRef, {
      metrics: {
        templateUsage: {
          total: 0,
          successful: 0,
          failed: 0,
          byTemplate: {},
          inProgress: {} // Track in-progress executions
        },
        creditUsage: {
          total: 0,
          monthly: {}
        },
        lastUpdated: Timestamp.now()
      }
    });
    return true;
  }
  return false;
};

// Update shop metrics summary using transaction
const updateShopMetricsSummary = async (shopId, updates) => {
  try {
    const shopRef = doc(db, 'shops', shopId);
    
    await runTransaction(db, async (transaction) => {
      const shopDoc = await transaction.get(shopRef);
      
      // Initialize metrics if needed
      if (!shopDoc.exists()) throw new Error('Shop not found');
      
      const shopData = shopDoc.data();
      if (!shopData.metrics) {
        transaction.set(shopRef, {
          metrics: {
            templateUsage: {
              total: 0,
              successful: 0,
              failed: 0,
              byTemplate: {},
              inProgress: {}
            },
            creditUsage: {
              total: 0,
              monthly: {}
            },
            lastUpdated: Timestamp.now()
          }
        }, { merge: true });
      }

      // Apply updates using transaction
      transaction.update(shopRef, {
        ...updates,
        'metrics.lastUpdated': Timestamp.now()
      });
    });
  } catch (error) {
    console.error('Error updating shop metrics summary:', error);
    throw error;
  }
};

// Record template usage for a shop with concurrency control
export const recordTemplateUsage = async (userId, shopId, templateId, success = true, productId = null, creditCost = null) => {
  try {
    const shopRef = doc(db, 'shops', shopId);
    const templateStatsRef = doc(collection(db, 'templateStats'));
    
    await runTransaction(db, async (transaction) => {
      // Get current shop data
      const shopDoc = await transaction.get(shopRef);
      if (!shopDoc.exists()) throw new Error('Shop not found');
      
      // Initialize metrics if needed
      const shopData = shopDoc.data();
      if (!shopData.metrics?.templateUsage) {
        transaction.set(shopRef, {
          metrics: {
            templateUsage: {
              total: 0,
              successful: 0,
              failed: 0,
              byTemplate: {},
              inProgress: {},
              daily: {},
              monthly: {},
              creditUsage: {
                total: 0,
                daily: {},
                monthly: {}
              }
            }
          }
        }, { merge: true });
      }

      // Get current date for daily and monthly tracking
      const now = new Date();
      const monthKey = `${now.getFullYear()}-${(now.getMonth() + 1).toString().padStart(2, '0')}`;
      const dayKey = `${monthKey}-${now.getDate().toString().padStart(2, '0')}`;
      
      // Determine credit cost if not provided
      const creditsUsed = creditCost || 0;

      // Update template usage metrics
      const updates = {
        [`metrics.templateUsage.total`]: increment(1),
        [`metrics.templateUsage.${success ? 'successful' : 'failed'}`]: increment(1),
        [`metrics.templateUsage.byTemplate.${templateId}.total`]: increment(1),
        [`metrics.templateUsage.byTemplate.${templateId}.${success ? 'successful' : 'failed'}`]: increment(1),
        // Add daily and monthly tracking
        [`metrics.templateUsage.daily.${dayKey}.total`]: increment(1),
        [`metrics.templateUsage.daily.${dayKey}.${success ? 'successful' : 'failed'}`]: increment(1),
        [`metrics.templateUsage.monthly.${monthKey}.total`]: increment(1),
        [`metrics.templateUsage.monthly.${monthKey}.${success ? 'successful' : 'failed'}`]: increment(1),
        'metrics.lastUpdated': Timestamp.now()
      };
      
      // Add credit usage tracking if credits were used
      if (creditsUsed > 0) {
        // Update template-specific credit usage
        updates[`metrics.templateUsage.creditUsage.total`] = increment(creditsUsed);
        updates[`metrics.templateUsage.creditUsage.daily.${dayKey}`] = increment(creditsUsed);
        updates[`metrics.templateUsage.creditUsage.monthly.${monthKey}`] = increment(creditsUsed);
        
        // Also update the general credit usage with type 'template' for the pie chart
        if (!shopData.metrics?.creditUsage?.byType) {
          transaction.set(shopRef, {
            metrics: {
              creditUsage: {
                byType: {
                  template: 0,
                  image: 0,
                  other: 0
                }
              }
            }
          }, { merge: true });
        }
        
        // Update the template type in the byType categorization for the pie chart
        updates[`metrics.creditUsage.byType.template`] = increment(creditsUsed);
      }

      // Record detailed template usage stat
      const statData = {
        userId,
        shopId,
        templateId,
        success,
        productId,
        creditCost: creditsUsed,
        timestamp: Timestamp.now(),
        day: dayKey,
        month: monthKey,
        usageType: 'template' // Add usage type for proper categorization
      };

      // Apply updates atomically (single update call)
      transaction.update(shopRef, updates);
      transaction.set(templateStatsRef, statData);
      
      // Also add a record in creditStats for the pie chart categorization
      if (creditsUsed > 0) {
        const creditStatsRef = doc(collection(db, 'creditStats'));
        transaction.set(creditStatsRef, {
          userId,
          shopId,
          creditsUsed,
          usageType: 'template',
          details: `Template execution: ${templateId}`,
          timestamp: Timestamp.now(),
          day: dayKey,
          month: monthKey
        });
      }
    });
  } catch (error) {
    console.error('Error recording template usage:', error);
    throw error;
  }
};

// Record credit usage for a shop with concurrency control
export const recordCreditUsage = async (userId, shopId, creditsUsed = 1, metadata = {}) => {
  try {
    const shopRef = doc(db, 'shops', shopId);
    const creditStatsRef = doc(collection(db, 'creditStats'));
    
    await runTransaction(db, async (transaction) => {
      // Get current shop data
      const shopDoc = await transaction.get(shopRef);
      if (!shopDoc.exists()) throw new Error('Shop not found');
      
      // Initialize metrics if needed
      const shopData = shopDoc.data();
      if (!shopData.metrics?.creditUsage) {
        transaction.set(shopRef, {
          metrics: {
            creditUsage: {
              total: 0,
              monthly: {},
              byType: {
                template: 0,
                image: 0,
                other: 0
              }
            }
          }
        }, { merge: true });
      }

      // Get current month key and day key for daily tracking
      const now = new Date();
      const monthKey = `${now.getFullYear()}-${(now.getMonth() + 1).toString().padStart(2, '0')}`;
      const dayKey = `${monthKey}-${now.getDate().toString().padStart(2, '0')}`;

      // Determine usage type (template, image, or other)
      const usageType = metadata?.usageType || 'other';
      
      // Update credit usage metrics
      const updates = {
        'metrics.creditUsage.total': increment(creditsUsed),
        [`metrics.creditUsage.monthly.${monthKey}`]: increment(creditsUsed),
        [`metrics.creditUsage.daily.${dayKey}`]: increment(creditsUsed),
        [`metrics.creditUsage.byType.${usageType}`]: increment(creditsUsed),
        'metrics.lastUpdated': Timestamp.now()
      };

      // Record detailed credit usage stat
      const statData = {
        userId,
        shopId,
        creditsUsed,
        usageType,
        details: metadata?.details || '',
        timestamp: Timestamp.now(),
        day: dayKey,
        month: monthKey
      };

      // Apply updates atomically
      transaction.update(shopRef, updates);
      transaction.set(creditStatsRef, statData);
    });
  } catch (error) {
    console.error('Error recording credit usage:', error);
    throw error;
  }
};

/**
 * Update the credit usage type for existing credits without recording additional credits
 * This is used to properly categorize credits that have already been deducted
 * @param {string} userId - User ID
 * @param {string} shopId - Shop ID
 * @param {number} creditsUsed - Number of credits used (for reporting only, no new credits are deducted)
 * @param {string} usageType - Type of usage (template, image, other)
 * @param {string} details - Additional details about the usage
 * @returns {Promise<void>}
 */
export const updateCreditUsageType = async (userId, shopId, creditsUsed, usageType = 'image', details = '') => {
  try {
    if (!userId || !shopId) {
      console.error('User ID and Shop ID are required to update credit usage type');
      return;
    }
    
    const shopRef = doc(db, 'shops', shopId);
    
    // Get current date information for metrics
    const now = new Date();
    const monthKey = `${now.getFullYear()}-${(now.getMonth() + 1).toString().padStart(2, '0')}`;
    const dayKey = `${monthKey}-${now.getDate().toString().padStart(2, '0')}`;
    
    // Get the shop document to check if it exists and has metrics
    const shopDoc = await getDoc(shopRef);
    if (!shopDoc.exists()) {
      console.error(`Shop ${shopId} not found`);
      return;
    }
    
    const shopData = shopDoc.data();
    
    // Initialize metrics if needed
    if (!shopData.metrics?.creditUsage?.byType) {
      await setDoc(shopRef, {
        metrics: {
          creditUsage: {
            byType: {
              template: 0,
              image: 0,
              other: 0
            }
          }
        }
      }, { merge: true });
    }
    
    // Update only the byType field to categorize the credits correctly
    // This doesn't add new credits, just updates the type distribution
    await updateDoc(shopRef, {
      [`metrics.creditUsage.byType.${usageType}`]: increment(creditsUsed)
    });
    
    // Add a record in creditStats for reporting purposes
    // This is marked as a type update so it won't be counted as new credits
    const creditStatsRef = doc(collection(db, 'creditStats'));
    await setDoc(creditStatsRef, {
      userId,
      shopId,
      creditsUsed,
      usageType,
      details: details || `Type update for ${creditsUsed} credits to ${usageType}`,
      timestamp: Timestamp.now(),
      day: dayKey,
      month: monthKey,
      isTypeUpdate: true // Flag to indicate this is just a type update, not new credits
    });
    
    console.log(`Updated credit usage type to ${usageType} for ${creditsUsed} credits`);
    return true;
  } catch (error) {
    console.error('Error updating credit usage type:', error);
    return false;
  }
};
